/**
 * useAuth — Auth state, platform detection, and auth-related handlers.
 *
 * Two callers, one code path:
 *   - inside a gym (`useMainApp`), where `gymId` is set and the login bootstrap
 *     also seeds the member doc, the gym's user list and `users.gymIds`;
 *   - on the gym selector at "/" (`GymSelector`), called with `gymId: null`
 *     because there is no gym yet. Every gym-scoped write below is guarded on
 *     `gymId` and simply does not run — the user doc half is identical.
 *
 * The selector used to carry its own copy of this bootstrap. The copy drifted
 * (see `0cc4422` for the custom-name clobber it caused, `48638a3` for the
 * concurrent-write denial only this side was guarded against), which is why
 * there is one now.
 */
import { useState, useEffect, useCallback } from "react";
import { auth, googleProvider, appleProvider, db } from "../firebase";
import {
  signInWithPopup, signInWithRedirect,
  signOut, onAuthStateChanged, sendSignInLinkToEmail,
  isSignInWithEmailLink, signInWithEmailLink,
  signInWithEmailAndPassword, createUserWithEmailAndPassword
} from "firebase/auth";
import {
  doc, setDoc, getDoc, updateDoc, runTransaction,
  serverTimestamp, arrayUnion,
} from "firebase/firestore";
import { DEFAULT_SETTINGS, DEFAULT_GYM_META, DIFFICULTY, HOLD_COLORS } from "../constants";
import { generateBoulderName, generateSlug } from "../utils";
import { incrementShard } from "../utils/shardedCounter";
import { localStore } from "../utils/safeStorage";
import { stashOAuthConflict, linkPendingCredential } from "../utils/accountLinking";
import { logCrashError } from "../utils/crashLogger";
import { ensureAuthIdentities } from "../utils/ensureAuthIdentities";
import { completeEmailLinkSetup } from "../utils/authActions";
import { redirectResultOnce } from "../utils/redirectResultOnce";
import { logInteractiveSignIn, logLateInteractiveSignIn, signInMethodForProvider } from "../utils/signInAnalytics";
import { armLatePopupOutcome, supersedeLatePopupOutcome, isPopupClosedCode } from "../utils/popupAuthOutcome";
import { hasProvider, PROVIDER_GOOGLE, PROVIDER_APPLE } from "../utils/authIdentities";
import { readStoredTheme } from "../contexts/ThemeContext";
import { logEvent, setAnalyticsUserProperties, EVT } from "../analytics";

export function useAuth({ gymId, gymDoc, lang }) {
  // Initialize from auth.currentUser to avoid loading flash on re-navigation.
  // After first auth resolution, currentUser is synchronously available.
  const [authUser, setAuthUser] = useState(() => auth.currentUser);
  const [authLoading, setAuthLoading] = useState(() => !auth.currentUser);
  // Cached display name (uid-keyed) used purely as a header fallback so we show
  // the stored (possibly custom) name on first paint instead of flashing the
  // Google displayName until the users/{uid} snapshot resolves. Kept separate
  // from `userData` so it never makes `userData` truthy early (which would
  // briefly trip userData-gated UI like the push-notif discovery badge). On a
  // cold reload `auth.currentUser` is still null here, so the auth listener
  // below re-seeds once the uid is known.
  const [seedName, setSeedName] = useState(() => {
    const c = localStore.getJSON("sendly-user-name");
    if (c && auth.currentUser && c.uid === auth.currentUser.uid) return c.name;
    return null;
  });
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [authError, setAuthError] = useState("");
  // Password auth is test-only — controlled by env flag so production builds
  // don't expose the form. The real gating is Firebase Console's Email/Password
  // provider toggle; this just hides the UI on prod.
  const isPasswordAuthEnabled = import.meta.env.VITE_ENABLE_PASSWORD_AUTH === "true";
  // True when the sign-in link was opened but the original email is missing
  // from localStorage (different device / cleared storage). The login card
  // switches to a "confirm your email" flow instead of window.prompt.
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);

  // Platform detection
  const isStandalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
  const isIOSStandalone = typeof window !== "undefined" && window.navigator.standalone === true;
  const isIOS = typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent);

  // Strip Firebase's email-link query params (`apiKey`, `oobCode`, `mode`,
  // `continueUrl`, `lang`) but keep any unrelated query string the page used,
  // along with the original path + hash. This lets a deep-link visitor
  // (e.g. `/gym/<slug>` or `/gym/<slug>/event/<id>`) stay on that page after
  // signing in via email link instead of getting bounced back to `/`.
  const cleanedUrl = (mode /* "absolute" | "relative" */) => {
    const url = new URL(window.location.href);
    ["apiKey", "oobCode", "mode", "continueUrl", "lang"].forEach(p => url.searchParams.delete(p));
    const tail = url.pathname + (url.search ? url.search : "") + url.hash;
    return mode === "absolute" ? url.origin + tail : tail;
  };
  const cleanedPostSignInUrl = () => cleanedUrl("relative");

  // Email link sign-in callback + redirect result
  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      const href = window.location.href;
      // An "add email-link sign-in" link is indistinguishable from an ordinary
      // sign-in link, so check the recorded intent FIRST — otherwise this would
      // just sign the user in again and the provider would never get linked.
      completeEmailLinkSetup(href).then(({ handled, ok, errorKey }) => {
        if (handled) {
          window.history.replaceState(null, "", cleanedPostSignInUrl());
          if (!ok && errorKey) setAuthError(errorKey);
          return;
        }
        const email = localStore.get("emailForSignIn");
        if (email) {
          signInWithEmailLink(auth, email, href)
            .then((cred) => {
              logInteractiveSignIn(cred, "email_link");
              localStore.remove("emailForSignIn");
              window.history.replaceState(null, "", cleanedPostSignInUrl());
            })
            .catch(e => {
              console.error("Email link sign-in error:", e);
              setAuthError(
                e?.code === "auth/account-exists-with-different-credential" ? "login.accountLinkPrompt" :
                e?.code === "auth/invalid-action-code" ? "login.linkInvalid" :
                "login.signInFailed"
              );
            });
        } else {
          // Ask the user to confirm their email on the login card (no native prompt).
          setNeedsEmailConfirm(true);
        }
      });
    }
    // Shared with the other useAuth mount and with GlobalProfile — overlapping
    // getRedirectResult() calls race each other (see redirectResultOnce.js).
    // The `login` for a redirect sign-in is logged here, where its credential
    // arrives; the other mount gets the same object and is deduplicated.
    redirectResultOnce().then(cred => {
      logInteractiveSignIn(cred, signInMethodForProvider(cred?.providerId));
    }).catch(e => {
      // A redirect (Apple or Google) returning an email already tied to another
      // provider surfaces here (one-account-per-email). Stash the attempted
      // credential and ask the user to sign in with their existing method.
      if (e?.code === "auth/account-exists-with-different-credential") {
        stashOAuthConflict(e);
        setAuthError("login.accountLinkPrompt");
        return;
      }
      console.warn("Redirect sign-in:", e.code || e.message);
    });
  }, []);

  // Called from the login card when `needsEmailConfirm` is true.
  const handleConfirmEmail = useCallback(async () => {
    const email = emailInput.trim();
    if (!email) return;
    supersedeLatePopupOutcome();
    try {
      const cred = await signInWithEmailLink(auth, email, window.location.href);
      logInteractiveSignIn(cred, "email_link");
      localStore.remove("emailForSignIn");
      window.history.replaceState(null, "", cleanedPostSignInUrl());
      setNeedsEmailConfirm(false);
      setAuthError("");
    } catch (e) {
      console.error("Email confirm error:", e);
      setAuthError(
        e?.code === "auth/account-exists-with-different-credential" ? "login.accountLinkPrompt" :
        e?.code === "auth/invalid-email" ? "login.emailInvalid" :
        "login.signInFailed"
      );
    }
  }, [emailInput]);

  // Auth timeout — on slow networks (especially PWA first launch on iPad),
  // onAuthStateChanged can take many seconds. Releasing authLoading shows the
  // login screen, so we must not do it prematurely when a valid session is
  // simply slow to rehydrate — that reads as a surprise logout and the user
  // re-logs in needlessly (the tablet "kiléptetett" symptom). (D3) If a stored
  // Firebase auth session exists in localStorage, wait much longer before
  // falling back to the login screen; onAuthStateChanged normally resolves
  // first and clears authLoading well before the timeout. With no stored
  // session, keep the original short 8s so a genuinely-logged-out user isn't
  // stuck on a blank screen.
  useEffect(() => {
    if (!authLoading) return;
    const hasStoredSession = localStore.keys().some((k) => /^firebase:authUser:/.test(k));
    const timeout = setTimeout(() => setAuthLoading(false), hasStoredSession ? 30000 : 8000);
    return () => clearTimeout(timeout);
  }, [authLoading]);

  // Record "last app open" (users/{uid}.lastLoginAt), throttled to at most one
  // write per LOCAL day per device. Fires on mount (cold boot) AND on
  // foreground (visibilitychange → visible). The foreground handler is the key
  // bit for PWAs: a standalone PWA that's merely backgrounded and resumed does
  // NOT re-fire onAuthStateChanged (the React app stays mounted), so without
  // this a persistent PWA would only ever update lastLoginAt on a true cold
  // boot — and a user who "opens the app" daily without a fresh login would
  // look stale. The localStorage day-guard keeps this to one small user-doc
  // write per day. MAU is unaffected (trackMAU is action-based, not login-based).
  useEffect(() => {
    if (!authUser) return;
    const uid = authUser.uid;
    const KEY = "sendly-lastlogin-day";
    const maybePing = () => {
      if (document.visibilityState !== "visible") return;
      const d = new Date();
      const today = `${uid}:${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const last = localStore.get(KEY);
      if (last === today) return; // already recorded today on this device
      localStore.set(KEY, today);
      // updateDoc (not setDoc) so a race ahead of the new-user doc creation just
      // fails silently instead of creating a malformed partial user doc — new
      // users already get lastLoginAt from their initial setDoc.
      updateDoc(doc(db, "users", uid), { lastLoginAt: serverTimestamp() }).catch(() => {});
    };
    maybePing();
    document.addEventListener("visibilitychange", maybePing);
    return () => document.removeEventListener("visibilitychange", maybePing);
  }, [authUser]);

  // Auth state listener — user/member/gym doc init
  useEffect(() => {
    // The listener fires more than once for the SAME user without a sign-out in
    // between: `linkPendingCredential` below calls linkWithCredential, which
    // mutates the auth user and re-fires this callback. Without a guard the whole
    // read-then-create pass ran twice concurrently, both passes read "no user
    // doc", and the loser's write landed as an UPDATE — denied by the `name` rule
    // for anyone whose name isn't the Google token name (production 2026-08-17:
    // auth-bootstrap / users.create), which aborted the member doc and gymIds
    // writes with it. Keyed by uid so a real account switch still bootstraps; the
    // effect re-runs per gymId, so entering another gym re-arms it. The guard is
    // set BEFORE the first await, so a re-fire during a running pass returns
    // immediately. The gym selector runs this same guard — it used to carry its
    // own `bootstrappedRef` copy of it.
    let bootstrappedUid = null;
    const unsub = onAuthStateChanged(auth, async (user) => {
      // Firebase DISCARDS this callback's return value, so without this try
      // any rejection in the bootstrap below is an unhandled rejection: it lands
      // in the crash log as a bare "Missing or insufficient permissions" with no
      // app frames and no clue which write failed — which is exactly what
      // production produced. `step` names the write; keep it accurate when
      // adding one.
      let step = "start";
      try {
        setAuthUser(user); setAuthLoading(false);
        if (!user) bootstrappedUid = null;
        // Cold reload (gym deep-link / refresh): auth.currentUser was null when
        // the useState initializer ran, so seed the cached name now that the uid
        // is known — before the users/{uid} snapshot resolves — to avoid the
        // brief Google-displayName flash in the header. `prev || …` never
        // clobbers an already-loaded doc.
        if (user) {
          const c = localStore.getJSON("sendly-user-name");
          if (c && c.uid === user.uid) setSeedName(prev => prev || c.name);
          // If a prior sign-in collided with this email (now re-authed via the
          // existing provider), attach the stashed provider so the same email
          // keeps one account. Best-effort — the user is already on the right one.
          linkPendingCredential(user).catch(() => {});
        }
        if (user && bootstrappedUid !== user.uid) {
          bootstrappedUid = user.uid;
          // Names the write we are on, so a rules denial in here says WHICH one
          // instead of arriving as a bare "Missing or insufficient permissions".
          step = "read";
          const userRef = doc(db, "users", user.uid);
          // No gym in context (the selector at "/") — the member and gym-parent
          // reads have nothing to point at, so they are skipped along with every
          // write that depends on them.
          const memberRef = gymId ? gymDoc("members", user.uid) : null;
          const gymParentRef = gymId ? doc(db, "gyms", gymId) : null;

          // Parallel fetch: user doc, member doc, gym parent doc
          const [userSnap, memberSnap, gymParentSnap] = await Promise.all([
            getDoc(userRef),
            memberRef ? getDoc(memberRef) : null,
            gymParentRef ? getDoc(gymParentRef) : null,
          ]);

          // This session's UI preferences, persisted onto the user doc so the
          // server side (recap mails, the per-gym push fan-out) knows which
          // language to write in. Only ever used to FILL — a stored preference
          // always wins, the same way a stored name does.
          const sessionLang = lang || "hu";
          const sessionTheme = readStoredTheme();

          const providerId = user.providerData?.[0]?.providerId;
          const authMethod = providerId === "google.com" ? "google"
            : providerId === "apple.com" ? "apple"
            : "email_link";

          // User doc: create or update.
          // A stored name wins over the Google profile name — it may be a
          // moderated custom name the user set in Settings, which must not be
          // clobbered. Falls back to the Google display name, then a generated
          // boulder name (email-link users have no displayName).
          //
          // `existing` is the doc as it stands NOW: the read above, or — when our
          // create loses a race — whatever the winning pass wrote.
          let existing = userSnap.exists() ? userSnap.data() : null;
          let resolvedName = existing?.name || user.displayName || generateBoulderName(lang);
          if (!existing) {
            step = "users.create";
            // Create ONLY while the doc is genuinely absent. A plain setDoc on a
            // doc that appeared between our read and this write is an UPDATE in
            // rules terms: it would clobber the winner's fields (no merge) and,
            // for a user whose name is not the Google token name (email-link /
            // Apple → a generated boulder name), the `name` rule denies it
            // outright. The transaction re-reads at commit time, so a doc that
            // appeared meanwhile falls through to the login path instead.
            const winner = await runTransaction(db, async (tx) => {
              const fresh = await tx.get(userRef);
              if (fresh.exists()) return fresh.data();
              tx.set(userRef, { uid: user.uid, name: resolvedName, authName: user.displayName || null, email: user.email, photoURL: user.photoURL || null, lastGymId: gymId || null, lang: sessionLang, theme: sessionTheme, createdAt: serverTimestamp(), lastLoginAt: serverTimestamp() });
              return null;
            });
            if (winner) {
              // Lost the race. Adopt the STORED name: the boulder name we just
              // generated was never written, and carrying it on would give the
              // member doc a different name than the user doc — the exact kind of
              // name flip-flop the stored-name-wins rule exists to prevent.
              existing = winner;
              resolvedName = winner.name || resolvedName;
            } else {
              logEvent(EVT.SIGN_UP, { method: authMethod });
            }
          }
          if (existing) {
            // The monogram avatar preference nulls the doc photoURL — don't
            // re-sync the provider photo over it on login.
            const photoSync = existing.avatarPreference === "monogram" ? null : (user.photoURL || null);
            step = "users.login";
            const loginUpdate = { photoURL: photoSync };
            // Only a real gym visit moves `lastGymId` — signing in on the
            // selector must not erase which gym the user was last in, since
            // that is what the selector prefetches on the next visit.
            if (gymId) loginUpdate.lastGymId = gymId;
            // Legacy docs created before these fields existed: fill from this
            // session, never overwrite.
            if (!existing.lang) loginUpdate.lang = sessionLang;
            if (!existing.theme) loginUpdate.theme = sessionTheme;
            await updateDoc(userRef, loginUpdate);
            // Re-sync the Google profile name on login — but only until the user
            // has chosen a custom name in Settings (nameCustom). Done as a
            // separate best-effort write so the `name` security rule (a client
            // may only write a name equal to the Google token name) can never
            // fail the core login update. user.displayName is set only for
            // Google sign-in; email-link users keep their stored name.
            if (user.displayName && existing.nameCustom !== true && existing.name !== user.displayName) {
              updateDoc(userRef, { name: user.displayName }).catch(() => {});
            }
            // Persist the Google/Auth display name separately (independent of
            // nameCustom, which locks `name`) so platform moderation can offer
            // "restore original name" without an Auth lookup. Separate best-effort
            // write so the `authName == token.name` rule can't fail the core login
            // update.
            if (user.displayName && existing.authName !== user.displayName) {
              updateDoc(userRef, { authName: user.displayName }).catch(() => {});
            }
          }

          // Refresh the sign-in address index when it's stale. Compared locally
          // against `user.providerData`, so an account already in sync costs
          // nothing; only a linked/unlinked provider, a completed email change,
          // or an account pre-dating the index triggers the callable. Fire and
          // forget — a search-convenience index must never delay sign-in.
          ensureAuthIdentities(user, existing ? existing.emails : null);

          // Sync analytics user properties. Deliberately NOT switched to
          // `sessionLang` / `sessionTheme`: those default to "hu" / "system",
          // while this has always reported "light" for a device with no stored
          // theme. Changing it would move a GA user property's distribution
          // under a refactor, which is not this commit's business.
          const existingData = existing || {};
          setAnalyticsUserProperties({
            preferred_language: existingData.lang || localStore.get("sendly-lang") || "hu",
            theme_preference: existingData.theme || localStore.get("sendly-theme") || "light",
            // On the selector nothing is being joined, so the count is just
            // what the user doc already says.
            gym_count: (existingData.gymIds?.length || 0) + (gymId && !memberSnap.exists() ? 1 : 0),
          });

          // ── Everything below is gym-scoped ──────────────────────────────
          // The selector ("/") signs the same user in with no gym in context:
          // there is no member doc to seed, no gym to add to `gymIds`, and no
          // gym parent to migrate.
          if (!gymId) return;

          // Member doc: create if needed. Self-join is always role "user" —
          // there is no "first member becomes gymAdmin" anymore (2026-09-24):
          // an admin-less gym can no longer be created, so no self-join ever
          // needs to promote itself. The rules enforce this shape too
          // (`hasOnly(['role','displayName','lang','joinedAt'])` on this
          // branch), so adding a fifth key here would fail server-side.
          // Always sync displayName so member lookups have the name available
          if (!memberSnap.exists()) {
            // `lang` is denormalized onto the member doc so the per-gym push
            // fan-out can build notifications in the member's language without an
            // N+1 user-doc read (CF falls back to the user doc if it's missing).
            const memberLang = existing?.lang || lang || "hu";
            step = "member.create";
            await setDoc(memberRef, { role: "user", displayName: resolvedName, lang: memberLang, joinedAt: serverTimestamp() });
            // Contact email lives in the gym-admin-readable memberContacts doc
            // (split out of the public member doc). First-join write here; later
            // email changes are fanned out by the syncMemberDisplayName trigger.
            if (user.email) {
              setDoc(doc(db, "gyms", gymId, "memberContacts", user.uid), { email: user.email }, { merge: true }).catch(() => {});
            }
            incrementShard(doc(db, "gyms", gymId), "totalUsers", 1).catch(() => {});
            logEvent(EVT.GYM_JOIN, {
              gym_id: gymId,
              is_first_gym: !existing || !existing.gymIds?.length,
            });
          }

          // Detect banned state — if the member doc exists and has banned=true,
          // the BannedScreen will render instead of the app. We must NOT add
          // the gymId back into users.gymIds (the ban CF explicitly removes it)
          // and must NOT sync displayName into the member doc (the server is
          // the source of truth while banned).
          const isBannedMember = memberSnap.exists() && memberSnap.data().banned === true;

          // Sync member displayName on every login (covers name changes and
          // legacy members without a name). Email is no longer stored on the
          // member doc — it lives in the gym-admin-readable memberContacts
          // subcollection (seeded on join / by the backfill, kept fresh by the
          // syncMemberDisplayName trigger).
          if (memberSnap.exists() && !isBannedMember && memberSnap.data().displayName !== resolvedName) {
            updateDoc(memberRef, { displayName: resolvedName }).catch(() => {});
          }

          // Add gymId to user's gym list (skip when banned — the ban CF already
          // removed it and the user shouldn't see the gym in their selector).
          if (!isBannedMember) {
            step = "users.gymIds";
            await updateDoc(userRef, { gymIds: arrayUnion(gymId) });
          }

          // Gym parent doc: create or migrate settings — platform admins ONLY.
          //
          // `gyms/{gymId}` create is `isPlatformAdmin()`, and update is
          // platformAdmin or a member touching ONLY `stats`. So for everyone else
          // these two writes are a GUARANTEED rules denial — and being bare
          // `await`s inside this callback, the denial used to abort the rest of
          // the bootstrap and surface as an anonymous unhandled rejection.
          //
          // They are a bootstrap/migration path from before gyms were created
          // through the platform admin panel; an ordinary login has no business
          // attempting either. Gating here means a normal user never issues a
          // write the rules will refuse, matching the same rule-mirroring
          // principle the memberContacts and events gates follow.
          const canBootstrapGym = existing?.platformRole === "platformAdmin";
          if (canBootstrapGym && !gymParentSnap.exists()) {
            step = "gym.create";
            await setDoc(gymParentRef, {
              ...DEFAULT_GYM_META,
              name: DEFAULT_GYM_META.name,
              slug: generateSlug(DEFAULT_GYM_META.name),
              settings: { ...DEFAULT_SETTINGS, customDifficulty: DIFFICULTY, customHoldColors: HOLD_COLORS },
              createdBy: user.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
            });
          } else if (canBootstrapGym && gymParentSnap.exists() && !gymParentSnap.data().settings) {
            step = "gym.settings";
            const oldSettingsSnap = await getDoc(gymDoc("settings", "gym"));
            const settingsData = oldSettingsSnap.exists()
              ? oldSettingsSnap.data()
              : { ...DEFAULT_SETTINGS, customDifficulty: DIFFICULTY, customHoldColors: HOLD_COLORS };
            await updateDoc(gymParentRef, { settings: settingsData });
          }
        }
      } catch (err) {
        // A bootstrap that died halfway (offline, a denied write) must stay
        // retryable on the next firing instead of leaving the session half-done.
        bootstrappedUid = null;
        logCrashError(err, { source: "auth-bootstrap", info: step });
      }
    });
    return unsub;
  }, [gymId]);

  /**
   * A popup the SDK settled as "closed" may still be signing the user in on a
   * weak network (popupAuthOutcome.js). Listen for that late answer and report
   * what it really ended in — the session itself comes from onAuthStateChanged.
   */
  const armLateSignIn = (code, providerId, method) => armLatePopupOutcome(code, {
    succeeded: () => hasProvider(auth.currentUser, providerId),
    onSettled: (ok) => {
      logEvent(EVT.AUTH_POPUP_LATE, { kind: "sign_in", outcome: ok ? "success" : "failure", method });
      if (ok) logLateInteractiveSignIn(auth.currentUser, method);
      // The SDK swallowed the real reason, so only the generic text is left.
      else setAuthError("login.signInFailed");
    },
  });

  const handleLogin = async () => {
    supersedeLatePopupOutcome();
    setAuthError("");
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      logInteractiveSignIn(cred, "google");
    } catch (e) {
      // Email already tied to another provider (e.g. an existing Apple account):
      // stash the Google credential and prompt the user to sign in with their
      // existing method, which links Google to that account.
      if (e?.code === "auth/account-exists-with-different-credential") {
        stashOAuthConflict(e);
        setAuthError("login.accountLinkPrompt");
        return;
      }
      // iOS standalone: popup fails, fall back to redirect
      if (isIOSStandalone && !auth.currentUser) {
        signInWithRedirect(auth, googleProvider);
        return;
      }
      console.error(e);
      // "Closed" shows nothing now — usually the user backed out, but on a weak
      // network the sign-in may still be in flight and answer later.
      if (isPopupClosedCode(e?.code)) {
        armLateSignIn(e.code, PROVIDER_GOOGLE, "google");
        return;
      }
      setAuthError(e?.code === "auth/network-request-failed" ? "login.networkError" : "login.signInFailed");
    }
  };

  const handleAppleLogin = async () => {
    supersedeLatePopupOutcome();
    setAuthError("");
    try {
      // Mirror the proven Google flow: popup first on every platform (it returns
      // cleanly to the PWA and completes via postMessage), and only fall back to
      // redirect when the popup actually fails in iOS standalone. Redirect-first
      // breaks in iOS standalone because the cross-origin round-trip can open in
      // Safari and lose the PWA's persisted pending-redirect state.
      const cred = await signInWithPopup(auth, appleProvider);
      logInteractiveSignIn(cred, "apple");
    } catch (e) {
      // Email already tied to another provider: stash the Apple credential and
      // prompt the user to sign in with their existing method, which links it.
      if (e?.code === "auth/account-exists-with-different-credential") {
        stashOAuthConflict(e);
        setAuthError("login.accountLinkPrompt");
        return;
      }
      // iOS standalone: popup failed (blocked / lost reference) → redirect.
      if (isIOSStandalone && !auth.currentUser) {
        signInWithRedirect(auth, appleProvider);
        return;
      }
      console.error(e);
      // "Closed" = the user backed out, or a weak network outlived the SDK's
      // 8 s grace and the sign-in answers later (see handleLogin).
      if (isPopupClosedCode(e?.code)) {
        armLateSignIn(e.code, PROVIDER_APPLE, "apple");
        return;
      }
      setAuthError(e?.code === "auth/network-request-failed" ? "login.networkError" : "login.signInFailed");
    }
  };

  const handleEmailLink = async () => {
    if (!emailInput.trim()) return;
    supersedeLatePopupOutcome();
    setAuthError("");
    try {
      // Use the current full URL (origin + path + search + hash, minus any
      // pre-existing auth params) as the continue URL so a deep-link visitor —
      // e.g. `/gym/<slug>` or `/gym/<slug>/event/<id>?utm=foo` — returns to the
      // same page after clicking the link in their inbox, instead of getting
      // kicked back to `/` (the GymSelector). The Firebase SDK appends its own
      // auth params on top; we strip them post-signin in `cleanedPostSignInUrl`.
      await sendSignInLinkToEmail(auth, emailInput.trim(), {
        url: cleanedUrl("absolute"),
        handleCodeInApp: true,
      });
      localStore.set("emailForSignIn", emailInput.trim());
      setEmailSent(true);
    } catch (e) {
      console.error(e);
      const code = e?.code || "";
      setAuthError(
        code === "auth/invalid-email" ? "login.emailInvalid" :
        code === "auth/network-request-failed" ? "login.networkError" :
        code === "auth/too-many-requests" ? "login.tooManyRequests" :
        "login.sendFailed"
      );
    }
  };

  const mapPasswordAuthError = (code) => (
    code === "auth/invalid-email" ? "login.emailInvalid" :
    code === "auth/missing-password" ? "login.passwordRequired" :
    code === "auth/weak-password" ? "login.passwordWeak" :
    code === "auth/email-already-in-use" ? "login.emailInUse" :
    code === "auth/invalid-credential" ? "login.invalidCredentials" :
    code === "auth/user-not-found" ? "login.invalidCredentials" :
    code === "auth/wrong-password" ? "login.invalidCredentials" :
    code === "auth/network-request-failed" ? "login.networkError" :
    code === "auth/too-many-requests" ? "login.tooManyRequests" :
    code === "auth/operation-not-allowed" ? "login.passwordAuthDisabled" :
    "login.signInFailed"
  );

  const handlePasswordSignIn = async () => {
    if (!isPasswordAuthEnabled) return;
    supersedeLatePopupOutcome();
    setAuthError("");
    try {
      const cred = await signInWithEmailAndPassword(auth, emailInput.trim(), passwordInput);
      logInteractiveSignIn(cred, "password");
    } catch (e) {
      console.error(e);
      setAuthError(mapPasswordAuthError(e?.code || ""));
    }
  };

  const handlePasswordSignUp = async () => {
    if (!isPasswordAuthEnabled) return;
    supersedeLatePopupOutcome();
    setAuthError("");
    try {
      await createUserWithEmailAndPassword(auth, emailInput.trim(), passwordInput);
    } catch (e) {
      console.error(e);
      setAuthError(mapPasswordAuthError(e?.code || ""));
    }
  };

  // `userData` clears itself: the provider drops it when auth goes null.
  const handleLogout = async () => { await signOut(auth); setAuthUser(null); };

  const handleSaveName = async (newName) => {
    if (!authUser) return { error: "save" };
    // Moderated save via the shared `setDisplayName` callable wrapper — see
    // src/utils/displayName.js for the full rationale (rules block direct
    // client writes of custom names).
    const { saveDisplayNameModerated } = await import("../utils/displayName");
    return saveDisplayNameModerated(newName);
  };


  return {
    authUser, setAuthUser, authLoading, seedName,
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent,
    authError, setAuthError,
    needsEmailConfirm, handleConfirmEmail,
    isStandalone, isIOSStandalone, isIOS,
    isPasswordAuthEnabled,
    handleLogin, handleAppleLogin, handleEmailLink, handleLogout, handleSaveName,
    handlePasswordSignIn, handlePasswordSignUp,
  };
}
